import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MCQQuestion, DescriptiveQuestion } from '../components/AssessmentQuestion';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle, AlignLeft, ListChecks, Clock, AlertTriangle, Heart, Lock } from 'lucide-react';
import { getAssessmentQuestions, checkAIContent } from '../utils/assessmentQuestions';
import { 
  saveAssessmentResult, 
  calculateMCQScore, 
  evaluateDescriptiveAnswers, 
  evaluatePersonalityAnswers,
  calculateOverallScore 
} from '../utils/assessmentEvaluation';
import { v4 as uuidv4 } from 'uuid';

const Assessment = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [mcqAnswers, setMcqAnswers] = useState<Record<string, string>>({});
  const [descriptiveAnswers, setDescriptiveAnswers] = useState<Record<string, string>>({});
  const [personalityAnswers, setPersonalityAnswers] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('mcq');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiDetected, setAiDetected] = useState<Record<string, boolean>>({});
  const [hasTabSwitched, setHasTabSwitched] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [mcqQuestions, setMcqQuestions] = useState<any[]>([]);
  const [descriptiveQuestions, setDescriptiveQuestions] = useState<any[]>([]);
  const [personalityQuestions, setPersonalityQuestions] = useState<any[]>([]);

  const initializeQuestions = () => {
    const { mcqs, descriptive, personality } = getAssessmentQuestions();
    
    setMcqQuestions(mcqs);
    setDescriptiveQuestions(descriptive);
    setPersonalityQuestions(personality || []);
    
    const totalTimeInMinutes = mcqs.length * 1 + descriptive.length * 5 + (personality?.length || 0) * 3;
    setTimeRemaining(totalTimeInMinutes * 60);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const authToken = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!authToken || userType !== 'candidate') {
      toast.error('Please log in as a candidate to continue');
      navigate('/login');
      return;
    }
    
    const applicationData = sessionStorage.getItem('applicationData');
    if (!applicationData) {
      toast.error('You need to complete the job application first');
      setIsAuthorized(false);
      
      setTimeout(() => {
        navigate('/application');
      }, 2000);
      return;
    }
    
    setIsAuthorized(true);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setHasBlurred(true);
        toast.warning('Leaving the assessment tab is recorded and may affect your evaluation', {
          duration: 3000,
          position: 'bottom-center'
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const timer = setTimeout(() => {
      setIsLoading(false);
      initializeQuestions();
    }, 1500);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (mcqQuestions.length > 0 && !isLoading) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            setIsTimerExpired(true);
            toast.error('Time is up! Your answers will be submitted automatically.', {
              duration: 5000,
              position: 'bottom-center'
            });
            setTimeout(() => handleSubmit(true), 3000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [mcqQuestions, isLoading]);

  const handleMCQAnswer = (questionId: string, optionId: string) => {
    setMcqAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleDescriptiveAnswer = async (questionId: string, answer: string) => {
    setDescriptiveAnswers((prev) => ({ ...prev, [questionId]: answer }));
    
    if (answer.length > 100) {
      try {
        const result = await checkAIContent(answer);
        setAiDetected(prev => ({ ...prev, [questionId]: result.isAIGenerated }));
        
        if (result.isAIGenerated) {
          toast.warning('AI-generated content detected. Please provide your own answer.', {
            duration: 5000,
            position: 'bottom-center'
          });
        }
      } catch (error) {
        console.error('Error checking AI content:', error);
      }
    }
  };

  const handlePersonalityAnswer = async (questionId: string, answer: string) => {
    setPersonalityAnswers((prev) => ({ ...prev, [questionId]: answer }));
    
    if (answer.length > 100) {
      try {
        const result = await checkAIContent(answer);
        setAiDetected(prev => ({ ...prev, [questionId]: result.isAIGenerated }));
        
        if (result.isAIGenerated) {
          toast.warning('AI-generated content detected. Please provide your own answer.', {
            duration: 5000,
            position: 'bottom-center'
          });
        }
      } catch (error) {
        console.error('Error checking AI content:', error);
      }
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (!hasTabSwitched) {
      setHasTabSwitched(true);
    }
  };

  const isMCQComplete = () => {
    return mcqQuestions.every((q) => mcqAnswers[q.id]);
  };

  const isDescriptiveComplete = () => {
    return descriptiveQuestions.every((q) => 
      descriptiveAnswers[q.id] && descriptiveAnswers[q.id].length > 0
    );
  };

  const isPersonalityComplete = () => {
    return personalityQuestions.every((q) => 
      personalityAnswers[q.id] && personalityAnswers[q.id].length > 0
    );
  };

  const isAssessmentComplete = () => {
    return isMCQComplete() && isDescriptiveComplete() && isPersonalityComplete();
  };

  const handleSubmit = async (isAutoSubmit = false) => {
    if (!isAutoSubmit && !isAssessmentComplete()) {
      toast.error('Please answer all questions before continuing');
      return;
    }

    setIsSubmitting(true);

    const cheatingFlags = {
      tabSwitched: hasTabSwitched,
      blurred: hasBlurred,
      aiDetected: Object.values(aiDetected).some(val => val),
      timeExpired: isTimerExpired
    };

    try {
      const applicationDataStr = sessionStorage.getItem('applicationData');
      const applicationData = applicationDataStr ? JSON.parse(applicationDataStr) : {};
      
      const mcqScore = calculateMCQScore(mcqAnswers, mcqQuestions);
      
      const { score: descriptiveScore, evaluation: aiEvaluation } = 
        await evaluateDescriptiveAnswers(descriptiveAnswers, descriptiveQuestions);
      
      const { evaluation: personalityEvaluation } = 
        await evaluatePersonalityAnswers(personalityAnswers, personalityQuestions);
      
      const overallScore = calculateOverallScore(mcqScore, descriptiveScore);
      
      const assessmentResult = {
        candidateId: uuidv4(),
        candidateName: applicationData.name || 'Anonymous Candidate',
        jobId: applicationData.jobId || '1',
        jobTitle: applicationData.jobTitle || 'Unknown Position',
        mcqAnswers,
        descriptiveAnswers,
        personalityAnswers,
        mcqScore,
        descriptiveScore,
        overallScore,
        aiEvaluation,
        personalityEvaluation,
        completedAt: new Date().toLocaleString(),
        cheatingFlags
      };
      
      saveAssessmentResult(assessmentResult);
      
      const assessmentData = {
        mcqScore,
        descriptiveScore,
        overallScore,
        cheatingFlags
      };
      sessionStorage.setItem('assessmentData', JSON.stringify(assessmentData));
      
      setTimeout(() => {
        setIsSubmitting(false);
        navigate('/video-intro');
      }, 1500);
    } catch (error) {
      console.error('Error processing assessment:', error);
      toast.error('Error processing your assessment');
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/application');
  };

  if (isAuthorized === false) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
              <Lock className="h-8 w-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-apple-text mb-4">
              Access Restricted
            </h1>
            <p className="text-apple-text-secondary mb-6">
              You need to complete the job application before you can take the assessment.
              Redirecting you to the application page...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apple-blue mx-auto"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (isLoading || isAuthorized === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
              <Loader2 className="animate-spin text-apple-blue" size={28} />
            </div>
            <h2 className="text-xl font-medium text-apple-text mb-2">
              Preparing Your Assessment
            </h2>
            <p className="text-apple-text-secondary">
              We're loading questions specific to the position you applied for.
            </p>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-apple-text-secondary">
                  Step 3 of 4
                </span>
                <span className="text-sm font-medium text-apple-blue">
                  Technical Assessment
                </span>
              </div>
              <div className="bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-apple-blue h-full transition-all duration-500 ease-apple"
                  style={{ width: '75%' }}
                ></div>
              </div>
            </div>

            <div className={`mb-4 flex items-center justify-between px-4 py-3 rounded-lg ${
              timeRemaining < 300 ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
            }`}>
              <div className="flex items-center">
                <Clock className="mr-2" size={20} />
                <span className="font-medium">Time Remaining:</span>
              </div>
              <div className={`text-lg font-bold ${
                timeRemaining < 300 ? 'text-red-600 animate-pulse' : ''
              }`}>
                {formatTime(timeRemaining)}
              </div>
            </div>

            {hasBlurred && (
              <div className="mb-4 px-4 py-3 bg-amber-50 text-amber-700 rounded-lg flex items-center">
                <AlertTriangle className="mr-2 flex-shrink-0" size={20} />
                <p className="text-sm">
                  We've detected that you left the assessment tab. This activity is logged and may affect your evaluation.
                </p>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-semibold text-apple-text mb-2">
                  Technical Assessment
                </h1>
                <p className="text-apple-text-secondary mb-8">
                  Please answer the following questions to help us evaluate your technical skills and experience.
                  Answer all questions before the timer expires.
                </p>

                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                  <button
                    className={`flex items-center py-3 px-5 text-sm font-medium border-b-2 -mb-px transition-all flex-shrink-0 ${
                      activeTab === 'mcq'
                        ? 'border-apple-blue text-apple-blue'
                        : 'border-transparent text-apple-text-secondary hover:text-apple-text'
                    }`}
                    onClick={() => handleTabChange('mcq')}
                  >
                    <ListChecks size={18} className="mr-2" />
                    Multiple Choice
                    {isMCQComplete() && (
                      <CheckCircle size={14} className="ml-2 text-green-500" />
                    )}
                  </button>
                  <button
                    className={`flex items-center py-3 px-5 text-sm font-medium border-b-2 -mb-px transition-all flex-shrink-0 ${
                      activeTab === 'descriptive'
                        ? 'border-apple-blue text-apple-blue'
                        : 'border-transparent text-apple-text-secondary hover:text-apple-text'
                    }`}
                    onClick={() => handleTabChange('descriptive')}
                  >
                    <AlignLeft size={18} className="mr-2" />
                    Technical Questions
                    {isDescriptiveComplete() && (
                      <CheckCircle size={14} className="ml-2 text-green-500" />
                    )}
                  </button>
                  <button
                    className={`flex items-center py-3 px-5 text-sm font-medium border-b-2 -mb-px transition-all flex-shrink-0 ${
                      activeTab === 'personality'
                        ? 'border-apple-blue text-apple-blue'
                        : 'border-transparent text-apple-text-secondary hover:text-apple-text'
                    }`}
                    onClick={() => handleTabChange('personality')}
                  >
                    <Heart size={18} className="mr-2" />
                    Behavioral Questions
                    {isPersonalityComplete() && (
                      <CheckCircle size={14} className="ml-2 text-green-500" />
                    )}
                  </button>
                </div>

                <div className={activeTab === 'mcq' ? 'block' : 'hidden'}>
                  {mcqQuestions.map((question) => (
                    <MCQQuestion
                      key={question.id}
                      question={question.question}
                      options={question.options}
                      onAnswer={(optionId) => handleMCQAnswer(question.id, optionId)}
                      selectedOption={mcqAnswers[question.id]}
                    />
                  ))}
                </div>

                <div className={activeTab === 'descriptive' ? 'block' : 'hidden'}>
                  {descriptiveQuestions.map((question) => (
                    <DescriptiveQuestion
                      key={question.id}
                      question={question.question}
                      onAnswer={(answer) => handleDescriptiveAnswer(question.id, answer)}
                      answer={descriptiveAnswers[question.id] || ''}
                      isAIDetected={aiDetected[question.id]}
                      preventPaste={true}
                    />
                  ))}
                </div>

                <div className={activeTab === 'personality' ? 'block' : 'hidden'}>
                  {personalityQuestions.map((question) => (
                    <DescriptiveQuestion
                      key={question.id}
                      question={question.question}
                      onAnswer={(answer) => handlePersonalityAnswer(question.id, answer)}
                      answer={personalityAnswers[question.id] || ''}
                      isAIDetected={aiDetected[question.id]}
                      preventPaste={true}
                    />
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="order-2 sm:order-1 apple-btn-secondary flex items-center justify-center"
                  >
                    <ArrowLeft className="mr-2" size={18} />
                    <span>Back</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    disabled={!isAssessmentComplete() || isSubmitting}
                    className={`order-1 sm:order-2 apple-btn flex items-center justify-center ${
                      !isAssessmentComplete() || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Continue to Video Intro</span>
                        <ArrowRight className="ml-2" size={18} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Assessment;
